import * as React from 'react';
import FocusLock, { MoveFocusInside } from 'react-focus-lock';
import { ClickAwayListener, withStyles } from '@material-ui/core';
import LookupPanel from 'components/common/LookupPanel';
import CreditNotesSearch from './CreditNotesSearch';
import ICreditNotesLookupPanelProps, { ICreditNotesLookupPanelHandle } from './CreditNotesLookupPanel.properties';
import { ICreditNotesSearchHandle } from './CreditNotesSearch/CreditNotesSearch.properties';
import ParkedCreditNotes from './ParkedCreditNotes';
import Scrollbars from 'react-custom-scrollbars';
import inlineStyles from './CreditNotesLookupPanel.styles';
import { CreditNotesActionTypes } from '../CreditNotes.actions';
import { useCNContextSelector, useCNDispatch } from '../CreditNotes.context';
import { useRetrieveCreditNotesContext, useRetrieveCreditNotesSummary } from 'api/creditNotes/creditNotes';
import { showSnackBar } from 'components/common/SnackBars/SnackBar.hooks';

function CreditNotesLookupPanel(props: ICreditNotesLookupPanelProps, ref: React.Ref<ICreditNotesLookupPanelHandle>): JSX.Element {
    const {
        open, classes, loading, parkedCreditNotesItems, parkedCreditNotesItemsLoading, onApplyCreditNotesContext,
        onOpenChanged: onToggleOpen
    }: ICreditNotesLookupPanelProps = props;
    const CreditNotesSearchRef = React.useRef<ICreditNotesSearchHandle>();

    const CreditNotesId = useCNContextSelector<'CreditNote'>((state) => state.CreditNote);
    const CreditNotesContextInline = useCNContextSelector<'CreditNotesContext'>((state) => state.CreditNotesContext);

    const [onFirstTimeLoaded, setOnFirstTimeLoad] = React.useState<boolean>(true);
    const contextDispatch = useCNDispatch();

    const retrieveCreditNotesContextMutation = useRetrieveCreditNotesContext();
    const fetchSummary = useRetrieveCreditNotesSummary(CreditNotesId);

    const [initialData, setInitialData] = React.useState();

    React.useEffect(
        () => {
            if (CreditNotesId !== undefined) {
                if (CreditNotesId > 0 && onFirstTimeLoaded) {
                    loadReceipt(CreditNotesId);
                } else {
                    retrieveCreditNotesContextMutation.mutateAsync({ WorkCreditNoteId: CreditNotesId }).then((response) => {
                        contextDispatch({
                            [CreditNotesActionTypes.setCreditNotesContext]: response?.CreditNotesContext?.inlineObject,
                        });
                        setInitialData(response?.CreditNotesContext?.inlineObject);
                    });
                }
                setOnFirstTimeLoad(false);
            }
        },
        [CreditNotesId]
    );

    async function handleRecordChange(recordId: any): Promise<void> {
        await loadReceipt(recordId);
    }

    async function loadReceipt(recordId: any): Promise<void> {
        const response = await retrieveCreditNotesContextMutation.mutateAsync({ WorkCreditNoteId: recordId });
        if (response?.Status) {
            contextDispatch({
                [CreditNotesActionTypes.setCreditNotesContext]: response.CreditNotesContext?.inlineObject,
                [CreditNotesActionTypes.setCreditNote]: recordId,
            });
            showSnackBar({ variant: 'success', message: 'Parked credit note recalled successfully.' });
            onToggleOpen(!response?.Status);
        }
    }

    React.useImperativeHandle(
        ref,
        () => ({
            focusWarehouse(): void {
                CreditNotesSearchRef.current?.focusOnInvoiceField();
            }
        })
    );

    async function handleApply(values: any): Promise<void> {
        if (onApplyCreditNotesContext) {
            const response = await onApplyCreditNotesContext(values);

            if (!response?.Status) {
                CreditNotesSearchRef.current?.focusOnInvoiceField();
            }
            if (response) {
                contextDispatch({
                    [CreditNotesActionTypes.setCreditNotesContext]: response.CreditNotesContext?.inlineObject,
                });
                fetchSummary.refetch();
            }
            onToggleOpen(!response?.Status);
        }
    }

    const warnChanges = () => {
        if (CreditNotesSearchRef.current?.isDirty()) {
            showSnackBar({ variant: 'warning', message: 'Please apply or undo changes.' });
        }
    };

    const handleOnClickAway = (e) => {
        const dialogElement = document.querySelector('.MuiDialog-container');
        if (open && !dialogElement?.contains(e.target)) { // check if click is not on dialog box otherwise show warning
            warnChanges();
        }
    };

    const formData = React.useMemo(
        () => {
            return {
                ...CreditNotesContextInline,
                ...(CreditNotesContextInline?.Invoice && {
                    Invoice: String(CreditNotesContextInline?.Invoice ?? ''),
                })
            };
        },
        [CreditNotesContextInline]);

    return (

        <ClickAwayListener onClickAway={handleOnClickAway} >

            <Scrollbars className={open ? classes.scrollBarStyleOpened : classes.scrollBarStyleClosed}
            >
                <LookupPanel open={open} setOpen={onToggleOpen}>
                    <FocusLock autoFocus={open}>
                        <MoveFocusInside>
                            <CreditNotesSearch
                                innerRef={CreditNotesSearchRef}
                                loading={loading}
                                formData={formData}
                                initialData={initialData}
                                onApply={handleApply}
                                onUndo={() => undefined}
                            />
                        </MoveFocusInside>
                    </FocusLock>
                    <ParkedCreditNotes
                        listItems={parkedCreditNotesItems}
                        isLoading={loading || parkedCreditNotesItemsLoading}
                        disabled={CreditNotesId > 0}
                        keyField={'WorkCreditNoteId'}
                        fieldsToDisplay={['Entity', 'DateTime', 'CustomerName', null]}
                        handleRecordChange={handleRecordChange}
                    />
                </LookupPanel>
            </Scrollbars>

        </ClickAwayListener>
    );
}

export default withStyles(inlineStyles, { index: 1 })(React.memo(React.forwardRef(CreditNotesLookupPanel)));
